<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { call } from '../api/http'
import { documents, listDocuments, openDocumentFile, uploadDocument, type Doc, type DocFilter } from '../api/documents'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import { formatMoney } from '../lib/money'
import { formatDate } from '../i18n'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import UiDrawer from '../components/UiDrawer.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { t, locale } = useI18n()
const session = useSession()
const load = useForm()
const upload = useForm()
const action = useForm()

const items = ref<Doc[]>([])
const cursor = ref<string | undefined>()
const filter = reactive<DocFilter>({ kind: '', status: '' })
const dragging = ref(false)
/** per-file upload outcome of the last batch */
const results = ref<{ name: string; created: boolean }[]>([])
const selected = ref<Doc | null>(null)

async function fetchPage(append: boolean) {
  await load.submit(async () => {
    const page = await listDocuments(filter, append ? cursor.value : undefined)
    items.value = append ? [...items.value, ...page.items] : page.items
    cursor.value = page.next_cursor
  })
}
watch(() => session.workspace?.id, () => fetchPage(false), { immediate: true })
watch(filter, () => fetchPage(false))

const upsert = (d: Doc) => {
  items.value = [d, ...items.value.filter((x) => x.id !== d.id)]
  if (selected.value?.id === d.id) selected.value = d
}

// Files go one by one so a failure (413/422) names its file and the rest still upload.
async function uploadFiles(files: FileList | File[] | null | undefined) {
  if (!files?.length) return
  results.value = []
  await upload.submit(async () => {
    for (const f of Array.from(files)) {
      const { doc, created } = await uploadDocument(f)
      upsert(doc)
      results.value = [...results.value, { name: f.name, created }]
    }
  })
}

function onDrop(e: DragEvent) {
  dragging.value = false
  uploadFiles(e.dataTransfer?.files)
}

const reextract = (d: Doc) =>
  action.submit(async () => upsert(await call(documents.POST('/documents/{id}/reextract', { params: { path: { id: d.id } } }))))

async function remove(d: Doc) {
  if (!confirm(t('documents.deleteConfirm', { name: d.file_name }))) return
  const ok = await action.submit(() => call(documents.DELETE('/documents/{id}', { params: { path: { id: d.id } } })))
  if (ok) {
    items.value = items.value.filter((x) => x.id !== d.id)
    selected.value = null
  }
}

const openFile = (d: Doc) => action.submit(() => openDocumentFile(d.id))

const statusVariant = (s: Doc['status']) => (s === 'extracted' ? 'success' : s === 'failed' ? 'danger' : 'secondary')
const pct = (c: number) => `${Math.round(c * 100)}%`
const kb = (n: number) => `${Math.max(1, Math.round(n / 1024))} KB`
</script>

<template>
  <div class="page wide">
    <header class="page-header">
      <div>
        <h1>{{ $t('documents.title') }}</h1>
        <p class="muted">{{ $t('documents.subtitle') }}</p>
      </div>
    </header>

    <UiCard v-if="session.canWrite">
      <label
        :class="['drop', { over: dragging }]"
        data-testid="doc-drop"
        @dragover.prevent="dragging = true"
        @dragleave="dragging = false"
        @drop.prevent="onDrop"
      >
        <input
          type="file"
          name="file"
          multiple
          accept="application/pdf,image/png,image/jpeg"
          class="sr-only"
          :disabled="upload.busy.value"
          @change="uploadFiles(($event.target as HTMLInputElement).files)"
        />
        <strong>{{ upload.busy.value ? $t('documents.uploading') : $t('documents.drop') }}</strong>
        <span class="muted small">{{ $t('documents.formats') }}</span>
      </label>
      <ul v-if="results.length" class="small results" data-testid="doc-upload-results">
        <li v-for="r in results" :key="r.name">{{ r.name }} — {{ r.created ? $t('documents.uploaded') : $t('documents.duplicate') }}</li>
      </ul>
      <ErrorBanner :error="upload.error.value" />
    </UiCard>

    <UiCard>
      <form class="filters" @submit.prevent>
        <select v-model="filter.kind" :aria-label="$t('documents.kind')">
          <option value="">{{ $t('documents.allKinds') }}</option>
          <option v-for="k in ['utility_bill', 'purchase_invoice', 'receipt', 'unknown']" :key="k" :value="k">{{ $t(`documents.kinds.${k}`) }}</option>
        </select>
        <select v-model="filter.status" :aria-label="$t('documents.status')">
          <option value="">{{ $t('documents.allStatuses') }}</option>
          <option v-for="s in ['received', 'extracted', 'failed']" :key="s" :value="s">{{ $t(`documents.statuses.${s}`) }}</option>
        </select>
      </form>
    </UiCard>

    <ErrorBanner :error="action.error.value ?? load.error.value" />

    <UiCard>
      <p v-if="load.busy.value && !items.length" class="muted">{{ $t('common.loading') }}</p>
      <EmptyState v-else-if="!items.length" :title="$t('documents.empty')" :message="$t('documents.emptyText')" />
      <div v-else class="table-wrap">
        <table class="table" data-testid="doc-table">
          <thead>
            <tr>
              <th>{{ $t('documents.file') }}</th>
              <th>{{ $t('documents.kind') }}</th>
              <th>{{ $t('documents.status') }}</th>
              <th class="num">{{ $t('documents.confidence') }}</th>
              <th class="num">{{ $t('documents.total') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in items" :key="d.id" data-testid="doc-row" class="clickable" @click="selected = d">
              <td>
                <button class="linkish" type="button">{{ d.file_name }}</button>
                <div class="muted small">{{ d.fields.issuer ?? '' }} · {{ formatDate(d.created_at) }}</div>
              </td>
              <td><UiBadge variant="outline">{{ $t(`documents.kinds.${d.kind}`) }}</UiBadge></td>
              <td><UiBadge :variant="statusVariant(d.status)" data-testid="doc-status">{{ $t(`documents.statuses.${d.status}`) }}</UiBadge></td>
              <td class="num">{{ pct(d.confidence) }}</td>
              <td class="num">{{ d.fields.total ? formatMoney(d.fields.total, locale) : '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="cursor" class="more">
        <UiButton variant="outline" :loading="load.busy.value" @click="fetchPage(true)">{{ $t('transactions.loadMore') }}</UiButton>
      </div>
    </UiCard>

    <UiDrawer v-if="selected" :title="selected.file_name" @close="selected = null">
      <div class="row">
        <UiBadge variant="outline">{{ $t(`documents.kinds.${selected.kind}`) }}</UiBadge>
        <UiBadge :variant="statusVariant(selected.status)">{{ $t(`documents.statuses.${selected.status}`) }}</UiBadge>
        <span class="muted small">{{ $t('documents.confidence') }} {{ pct(selected.confidence) }} · {{ kb(selected.size) }}</span>
      </div>
      <p v-if="selected.error" class="err small">{{ selected.error }}</p>
      <ErrorBanner :error="action.error.value" />
      <div class="row">
        <UiButton variant="outline" size="sm" @click="openFile(selected)">{{ $t('documents.openFile') }}</UiButton>
        <template v-if="session.canWrite">
          <UiButton variant="outline" size="sm" :loading="action.busy.value" @click="reextract(selected)">{{ $t('documents.reextract') }}</UiButton>
          <UiButton variant="destructive" size="sm" @click="remove(selected)">{{ $t('common.delete') }}</UiButton>
        </template>
      </div>

      <h3>{{ $t('documents.fields') }}</h3>
      <dl class="kv">
        <dt>{{ $t('bills.vendor') }}</dt>
        <dd>{{ selected.fields.issuer ?? '—' }}</dd>
        <dt>{{ $t('bills.invoiceNumber') }}</dt>
        <dd>{{ selected.fields.invoice_number ?? '—' }}</dd>
        <dt>{{ $t('bills.issueDate') }}</dt>
        <dd>{{ selected.fields.issue_date ?? '—' }}</dd>
        <dt>{{ $t('bills.dueDate') }}</dt>
        <dd>{{ selected.fields.due_date ?? '—' }}</dd>
        <dt>{{ $t('bills.period') }}</dt>
        <dd>{{ selected.fields.period_start ? `${selected.fields.period_start} – ${selected.fields.period_end ?? ''}` : '—' }}</dd>
        <dt>{{ $t('documents.total') }}</dt>
        <dd>{{ selected.fields.total ? formatMoney(selected.fields.total, locale) : '—' }}</dd>
        <template v-if="selected.fields.vat">
          <dt>{{ $t('bills.vat') }}</dt>
          <dd>
            {{ selected.fields.vat.rate_pct }}% ·
            {{ formatMoney({ amount_minor: selected.fields.vat.vat_minor, currency: selected.fields.total?.currency ?? 'EUR' }, locale) }}
          </dd>
        </template>
      </dl>

      <div v-if="selected.fields.lines.length" class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th>{{ $t('bills.description') }}</th>
              <th class="num">{{ $t('bills.qty') }}</th>
              <th>{{ $t('bills.unit') }}</th>
              <th class="num">{{ $t('bills.unitPrice') }}</th>
              <th class="num">{{ $t('bills.amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(l, i) in selected.fields.lines" :key="i">
              <td>{{ l.description }}</td>
              <td class="num">{{ l.qty ?? '' }}</td>
              <td>{{ l.unit ?? '' }}</td>
              <td class="num">{{ l.unit_price ?? '' }}</td>
              <td class="num">{{ formatMoney(l.amount, locale) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </UiDrawer>
  </div>
</template>

<style scoped>
.wide {
  max-width: 1200px;
}
.drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 2rem 1rem;
  border: 2px dashed var(--border);
  border-radius: var(--radius);
  text-align: center;
  cursor: pointer;
}
.drop.over,
.drop:hover {
  border-color: var(--accent);
}
.drop:focus-within {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.results {
  margin: 0.75rem 0 0;
  padding-left: 1.2rem;
}
.filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.5rem;
}
.clickable {
  cursor: pointer;
}
.clickable:hover td {
  background: var(--bg);
}
.linkish {
  border: 0;
  padding: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  text-align: left;
  cursor: pointer;
  word-break: break-word;
}
.err {
  color: var(--loss);
}
.more {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}
</style>
